<template>
  <div v-if="segments.length" class="note-text">
    <template v-for="(seg, i) in segments" :key="i">
      <div v-if="seg.type === 'text'" class="note-text__paragraph">{{ seg.text }}</div>
      <div v-else class="note-text__table-scroll">
        <table class="note-text__table">
          <tbody>
            <tr v-for="(row, r) in seg.rows" :key="r">
              <td v-for="(cell, c) in row" :key="c">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  text: {
    type: String,
    default: "",
  },
});

/**
 * Split a note into renderable segments. Tab-delimited runs (Excel / Google
 * Sheets paste) become tables; everything else is whitespace-preserving text.
 * All output is rendered via `{{ }}` interpolation — never v-html — so pasted
 * content cannot inject markup.
 */
function parseSegments(raw) {
  const text = String(raw ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  if (!text.trim()) return [];

  const segments = [];
  let textBuf = [];
  let tableBuf = [];

  const flushText = () => {
    if (!textBuf.length) return;
    // Collapse runs of 3+ blank lines and trim surrounding blanks so pasted
    // notes don't render with large vertical gaps.
    const joined = textBuf
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/^\n+|\n+$/g, "");
    if (joined.length) segments.push({ type: "text", text: joined });
    textBuf = [];
  };

  const flushTable = () => {
    if (!tableBuf.length) return;
    const rows = tableBuf.map((line) => line.split("\t").map((cell) => cell.trim()));
    const cols = rows.reduce((max, row) => Math.max(max, row.length), 0);
    rows.forEach((row) => {
      while (row.length < cols) row.push("");
    });
    segments.push({ type: "table", rows });
    tableBuf = [];
  };

  for (const line of text.split("\n")) {
    if (line.includes("\t")) {
      flushText();
      tableBuf.push(line);
    } else {
      flushTable();
      textBuf.push(line);
    }
  }
  flushText();
  flushTable();
  return segments;
}

const segments = computed(() => parseSegments(props.text));
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.note-text {
  min-width: 0;
}

.note-text__paragraph {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  & + .note-text__paragraph {
    margin-top: 6px;
  }
}

.note-text__table-scroll {
  overflow-x: auto;
  margin: 4px 0;

  & + .note-text__paragraph {
    margin-top: 6px;
  }
}

.note-text__table {
  border-collapse: collapse;
  font-family: $lh_font_mono;
  font-size: 0.92em;
  width: max-content;
  max-width: 100%;

  td {
    border: 1px solid $lh_dash_border;
    padding: 3px 8px;
    text-align: left;
    vertical-align: top;
    white-space: pre-wrap;
    word-break: break-word;
  }

  tr:first-child td {
    background: rgba($lh_color_navy, 0.04);
    font-weight: 600;
  }
}
</style>
